"use client";

import Link from "next/link";
import {
  Typography,
  Button,
  Steps,
  Row,
  Col,
  Card,
  Space,
  Divider,
} from "antd";
import { SiteHeader, SiteFooter } from "@/widgets/site-layout";
import { PRODUCT_NAME } from "@/shared/config/labels";

const { Title, Paragraph, Text } = Typography;

const steps = [
  { title: "Загрузка", description: "Загрузите DOCX-файл в личный кабинет" },
  { title: "Обработка", description: "AI разбирает структуру и извлекает элементы" },
  { title: "Редактирование", description: "Настройте кнопки, модальные окна и стиль" },
  { title: "Экспорт", description: "Получите HTML или PDF для публикации" },
];

export default function HomePage() {
  return (
    <>
      <SiteHeader />
      <main>
        {/* Hero */}
        <section style={{ background: "#ffffff", padding: "80px 24px", textAlign: "center" }}>
          <div style={{ maxWidth: 900, margin: "0 auto" }}>
            <Title level={1} style={{ fontSize: "clamp(2rem, 5vw, 3.5rem)", color: "#1677ff", marginBottom: 0 }}>
              Интерактивные веб-страницы
            </Title>
            <Paragraph style={{ fontSize: "clamp(1.25rem, 3vw, 1.75rem)", marginTop: 16, marginBottom: 0 }}>
              Превратите Word-документы в современный формат
            </Paragraph>
            <Paragraph type="secondary" style={{ fontSize: 18, marginTop: 24, maxWidth: 640, margin: "24px auto 0" }}>
              Загрузите DOCX — получите современную веб-версию с модальными окнами,
              интерактивными таблицами, сносками и вложениями.
            </Paragraph>
            <Space size="middle" style={{ marginTop: 40 }}>
              <Link href="/admin/login">
                <Button type="primary" size="large">Войти</Button>
              </Link>
              <Link href="/contact">
                <Button size="large">Запросить доступ</Button>
              </Link>
            </Space>
          </div>
        </section>

        <Divider style={{ margin: 0 }} />

        {/* Problem */}
        <section style={{ padding: "64px 24px", background: "#f5f5f5" }}>
          <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
            <Title level={2}>Проблема</Title>
            <Paragraph type="secondary" style={{ fontSize: 16, marginTop: 16 }}>
              Корпоративные документы — отчёты, регламенты, инструкции — остаются
              в формате PDF и Word. Пользователям неудобно искать информацию,
              таблицы и приложения теряются в длинных файлах.
            </Paragraph>
          </div>
        </section>

        {/* Solution */}
        <section style={{ padding: "64px 24px", background: "#ffffff" }}>
          <div style={{ maxWidth: 720, margin: "0 auto", textAlign: "center" }}>
            <Title level={2}>Решение</Title>
            <Paragraph type="secondary" style={{ fontSize: 16, marginTop: 16 }}>
              {PRODUCT_NAME} автоматически разбирает ваш DOCX, извлекает
              таблицы, изображения, ссылки и сноски, превращая их в интерактивные
              элементы с настраиваемым брендингом.
            </Paragraph>
          </div>
        </section>

        {/* How it works */}
        <section style={{ padding: "64px 24px", background: "#f5f5f5" }}>
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <Title level={2} style={{ textAlign: "center", marginBottom: 48 }}>
              Как это работает
            </Title>
            <Steps
              direction="horizontal"
              responsive
              items={steps}
              style={{ marginBottom: 48 }}
            />
            <Row gutter={[24, 24]}>
              {steps.map((item, i) => (
                <Col xs={24} sm={12} md={6} key={item.title}>
                  <Card style={{ textAlign: "center", height: "100%" }}>
                    <div
                      style={{
                        width: 48,
                        height: 48,
                        borderRadius: "50%",
                        background: "#1677ff",
                        color: "#fff",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 700,
                        fontSize: 18,
                        margin: "0 auto 16px",
                      }}
                    >
                      {i + 1}
                    </div>
                    <Title level={5}>{item.title}</Title>
                    <Text type="secondary">{item.description}</Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        {/* Before/After */}
        <section style={{ padding: "64px 24px", background: "#ffffff" }}>
          <div style={{ maxWidth: 1000, margin: "0 auto" }}>
            <Title level={2} style={{ textAlign: "center", marginBottom: 48 }}>
              До и после
            </Title>
            <Row gutter={[24, 24]}>
              <Col xs={24} md={12}>
                <Card title={<Text type="danger">Сложно читать</Text>}>
                  <ul style={{ paddingLeft: 20, margin: 0 }}>
                    <li><Text type="secondary">Статичный PDF на 200 страниц</Text></li>
                    <li><Text type="secondary">Таблицы в конце документа</Text></li>
                    <li><Text type="secondary">Нет поиска по содержимому</Text></li>
                    <li><Text type="secondary">Нет адаптивности для мобильных</Text></li>
                  </ul>
                </Card>
              </Col>
              <Col xs={24} md={12}>
                <Card
                  title={<Text style={{ color: "#52c41a" }}>После</Text>}
                  style={{ borderColor: "#91caff" }}
                >
                  <ul style={{ paddingLeft: 20, margin: 0 }}>
                    <li><Text type="secondary">Интерактивная веб-страница</Text></li>
                    <li><Text type="secondary">Таблицы открываются в модальных окнах</Text></li>
                    <li><Text type="secondary">Полнотекстовый поиск и оглавление</Text></li>
                    <li><Text type="secondary">Адаптивный дизайн с брендингом</Text></li>
                  </ul>
                  <div
                    style={{
                      marginTop: 20,
                      padding: "12px 16px",
                      borderRadius: 8,
                      background: "linear-gradient(135deg, #e6f4ff 0%, #f6ffed 100%)",
                      border: "1px solid #91caff",
                      textAlign: "center",
                    }}
                  >
                    <Text strong style={{ fontSize: 18, color: "#1677ff" }}>
                      Удобство чтения в 20× быстрее
                    </Text>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        </section>

        {/* Audience */}
        <section style={{ padding: "64px 24px", background: "#f5f5f5" }}>
          <div style={{ maxWidth: 1000, margin: "0 auto", textAlign: "center" }}>
            <Title level={2} style={{ marginBottom: 48 }}>Для кого</Title>
            <Row gutter={[24, 24]}>
              {[
                { title: "Корпорации", desc: "Регламенты, политики, отчёты для сотрудников" },
                { title: "Образование", desc: "Учебные материалы и методички" },
                { title: "Госсектор", desc: "Нормативные документы и справочники" },
              ].map((item) => (
                <Col xs={24} sm={8} key={item.title}>
                  <Card style={{ height: "100%" }}>
                    <Title level={4}>{item.title}</Title>
                    <Text type="secondary">{item.desc}</Text>
                  </Card>
                </Col>
              ))}
            </Row>
          </div>
        </section>

        {/* Access */}
        <section style={{ padding: "64px 24px", background: "#f5f5f5", textAlign: "center" }}>
          <div style={{ maxWidth: 640, margin: "0 auto" }}>
            <Title level={2}>Доступ к платформе</Title>
            <Paragraph type="secondary" style={{ fontSize: 16, marginTop: 16 }}>
              Самостоятельная регистрация недоступна. Администраторов компаний создаёт
              только суперадмин платформы — после согласования подключения вашей организации.
            </Paragraph>
            <Link href="/contact">
              <Button type="primary" size="large" style={{ marginTop: 24 }}>
                Запросить доступ
              </Button>
            </Link>
          </div>
        </section>

        {/* Contact CTA */}
        <section style={{ padding: "64px 24px", background: "#f5f5f5", textAlign: "center" }}>
          <div style={{ maxWidth: 480, margin: "0 auto" }}>
            <Title level={2}>Свяжитесь с нами</Title>
            <Paragraph type="secondary">Есть вопросы? Мы поможем подобрать решение</Paragraph>
            <Link href="/contact">
              <Button type="primary" size="large" style={{ marginTop: 24 }}>
                Написать нам
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </>
  );
}
